import React, { createContext, useState } from 'react'

import { Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { isFunction, isObject } from 'lodash'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import StudentForm, { Types } from './StudentForm'
import { Permissions } from './SearchStudentModalProvider'
import FormFooter from '../../../Components/antd/Forms/FormFooter'
import { StudentValidators } from '../../../utils/entities/student'
import { isValid } from '../../../utils/validators'
import { isOptimistic } from '../../../utils'
import { createStudent, updateStudent } from '../../../utils/api/student'
import { displayError, onError, onSuccess, onWarning } from '../../../utils/apiHelper'
import { saveStudent } from '../../../reducers/StudentsReducer/actions'
import { HttpStatuses } from '../../../utils/http'

export const StudentFormModalContext = createContext()

const DEFAULT_FORM_HANDLER = { data: null, callback: null, mode: Permissions.GOD }

/**
 * Business refusal of POST /api/students and PATCH /api/students/{id}: the body carries its code
 * (STUDENT_ALREADY_IN_SCHOOL, EMAIL_ALREADY_USED, INVALID_OR_DELETED_SECTION_PROVIDED…), translated
 * under `student.<action>.error.<code>`. The other statuses have nothing usable to display.
 */
const BUSINESS_ERROR_STATUSES = [HttpStatuses.BAD_REQUEST, HttpStatuses.FORBIDDEN, HttpStatuses.CONFLICT]

const StudentFormModal = ({ children, sectors = [], keysToValidate = [] }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const dispatch = useDispatch()

  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)
  const [errorFields, setErrorFields] = useState([])

  const handleSubmit = () => {
    const errors = isValid(formHandler.data, Object.values(StudentValidators))

    if (errors.keys.length === 0) {
      setErrorFields(errors.keys)

      let promise
      let action

      const body = formHandler.data.toRequestBody()

      if (isOptimistic(formHandler.data)) {
        promise = createStudent(user, body)
        action = 'create'
      } else {
        if (formHandler.data.sections[0]?.school?.premium) {
          delete body.sections
        }

        promise = updateStudent(user, formHandler.data, body)
        action = 'update'
      }

      promise.then(({ data }) => {
        dispatch(saveStudent(data, action === 'update'))

        if (isFunction(formHandler.callback)) {
          formHandler.callback(data, action)
        }

        setFormHandler(DEFAULT_FORM_HANDLER)
        onSuccess(t(`student.${action}.success`))

        if (action === 'create' && data.sections.length > 1) {
          onWarning(t('student.create.warning.multiple_schools'))
        }
      }).catch(error => {
        const errorKey = `student.${action}.error`

        if (BUSINESS_ERROR_STATUSES.includes(error.status)) {
          displayError(error, errorKey, errorKey)
        } else {
          onError(t(errorKey))
          console.error(error)
        }
      })
    } else {
      setErrorFields(errors.keys)
    }
  }

  return (
    <>
      <StudentFormModalContext.Provider value={{ setFormHandler }}>
        {children}
      </StudentFormModalContext.Provider>
      <Modal
        className='form-modal'
        title={t('student_form.title.' + (isOptimistic(formHandler.data) ? 'create' : 'update'))}
        visible={isObject(formHandler.data)}
        onCancel={() => setFormHandler(DEFAULT_FORM_HANDLER)}
        destroyOnClose
        footer={null}
      >
        <div className='pdl-16 pdr-16'>
          {isObject(formHandler.data) && (
            <StudentForm
              data={formHandler.data}
              errorFields={errorFields}
              permission={formHandler.mode}
              type={Types.STUDENT}
              onChange={data => setFormHandler(form => ({ ...form, data }))}
            />
          )}
        </div>
        <FormFooter
          cancel={{ text: t('modal.cancel_button') }}
          remove={null}
          submit={formHandler.mode !== Permissions.READ_ONLY && { text: t('modal.ok_button.save') }}
          onSubmit={handleSubmit}
          onCancel={() => setFormHandler(DEFAULT_FORM_HANDLER)}
        />
      </Modal>
    </>
  )
}

export default StudentFormModal
